import { MemoMarkdown } from "@/components/memo/MemoMarkdown";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";
import type { PrintLine } from "@/lib/memoPrint";

/**
 * One printed line of a Protokoll section. A point carries a bullet; an update is
 * indented plain; a to-do swaps the bullet for a checkbox glyph (☑/☐) and appends
 * its assignee and Frist. A retired point gets a ✓ and its closure date; a
 * historical / logged line carries a muted date tag. Body renders through the same
 * MemoMarkdown as the live memo. Purely presentational — no interactive controls.
 */
export function PrintLineView({ line }: { line: PrintLine }) {
  // If the body IS already a list (starts with a bullet/number), MemoMarkdown
  // draws its own markers — so a plain point drops its "•" to avoid a double
  // bullet, exactly as the on-screen MemoEntryRow gutter dot does. A to-do keeps
  // its checkbox regardless (the checkbox is not a bullet).
  const firstBodyLine = line.bodyMarkdown.split("\n").find((l) => l.trim()) ?? "";
  const startsWithList = /^\s*([-*+]|\d+[.)])\s/.test(firstBodyLine);

  // To-do → checkbox glyph; plain point → bullet (unless the body is a list);
  // update → no marker (indented).
  const marker = line.todo
    ? line.todo.done
      ? "☑"
      : "☐"
    : line.kind === "point" && line.indent === 0 && !startsWithList
      ? "•"
      : null;

  const todoMeta = line.todo
    ? ` — ${line.todo.assigneeName ?? ""}${
        line.todo.dueDate ? `, ${t.printDuePrefix} ${line.todo.dueDate}` : ""
      }`
    : null;

  return (
    <div
      // Carried-in historical (read-only) lines read muted — the same class the
      // closure/log tags use, so the whole line recedes.
      className={line.historical ? "memo-print-line memo-print-muted" : "memo-print-line"}
      data-indent={line.indent}
      style={{
        display: "flex",
        gap: 8,
        marginLeft: line.indent === 1 ? 18 : 0,
        padding: "2px 0",
        fontSize: 13,
      }}
    >
      {marker ? (
        <span className="memo-print-marker" style={{ flexShrink: 0, color: BRAND.blueGreyDk }}>
          {marker}
        </span>
      ) : null}

      <div
        className="memo-print-line-body"
        style={{
          flex: 1,
          minWidth: 0,
          // A closed point is struck + muted whether or not it carries a date.
          ...(line.closed ? { textDecoration: "line-through", color: BRAND.blueGrey } : {}),
        }}
      >
        <MemoMarkdown markdown={line.bodyMarkdown} />
        {todoMeta ? (
          <span
            className="memo-print-todo-meta"
            style={{ color: BRAND.blueGreyDk, fontWeight: 600 }}
          >
            {todoMeta}
          </span>
        ) : null}
        {line.closed ? (
          // ✓ always when closed; the "(abgeschlossen <date>)" parenthetical only
          // when a closure date exists (historical/merged entries may have none).
          <span className="memo-print-muted" style={{ color: BRAND.blueGrey }}>
            {` ✓${line.closedDate ? ` (${t.printClosedPrefix} ${line.closedDate})` : ""}`}
          </span>
        ) : null}
        {/* The bare, unlabeled log date is deliberately NOT printed — on paper it read
            as a stray date with no meaning. Only the LABELLED dates (fällig, abgeschlossen)
            appear. `line.logDate` stays in the model for potential future use. */}
      </div>
    </div>
  );
}
